<script setup>
    import SessionsSpeakers from '~/components/blocks/two-six/sessions/speakers.vue';
    import SessionsRelated from '~/components/blocks/two-six/sessions/related.vue';
    import SessionsTags from '~/components/blocks/two-five/sessions/tags.vue';
    import { getConferenceTimezone, getConferenceTimezoneLabel, isArchivedSession, isSessionOnDemand } from '~~/shared/utilities/schedule';

    const { $bus, $clientPosthog } = useNuxtApp();
    const dayjs = useDayjs();

    const props = defineProps({
        session_id: {
            type: String,
            default: '',
        },
    });

    const open = defineModel('open');
    const route = useRoute();
    const router = useRouter();

    const findMatch = (sessions, id) => {
        if (!Array.isArray(sessions)) return null;
        const needle = id?.toLowerCase?.();
        return sessions.find((s) => {
            const sid = s?.id?.toLowerCase?.();
            const uid = s?.unique_id?.toLowerCase?.();
            return sid === needle || uid === needle;
        }) || null;
    };

    const { data, pending, refresh } = useLazyAsyncData(
        `session-drawer-${props.session_id}`,
        async () => {
            const id = props.session_id;
            try {
                const res2026 = await $fetch('/api/v1/sessions', { params: { id, year: 2026, unscheduled: true } });
                const match2026 = findMatch(res2026?.sessions, id);
                if (match2026) return match2026;
            } catch {}
            try {
                const res2025 = await $fetch('/api/v1/sessions', { params: { id, year: 2025 } });
                const match2025 = findMatch(res2025?.sessions, id);
                if (match2025) return match2025;
            } catch {}
            return null;
        },
    );

    const session = computed(() => data.value || null);

    const shareLink = computed(() => {
        if (!session.value) return undefined;
        const slug = (session.value.unique_id || session.value.id || '').toLowerCase();
        if (!slug) return undefined;
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}/sessions/${slug}`;
    });

    const contentRef = ref(null);

    const scrollDrawerToTop = () => {
        if (!contentRef.value) return;
        let el = contentRef.value;
        while (el) {
            const style = getComputedStyle(el);
            const overflowY = style.overflowY;
            if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                el.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            el = el.parentElement;
        }
    };

    watch(() => props.session_id, (v) => {
        if (!v) return;
        refresh();
        nextTick(scrollDrawerToTop);
    });

    const drawerStyles = {
        content: 'bg-[var(--pink)] border-0 ring-0 rounded-none p-0 w-screen max-w-[95vw] md:max-w-[95vw] lg:max-w-[1120px]',
        overlay: 'bg-[var(--purple)]/80 backdrop-blur-xs',
        container: 'session-drawer-container gap-0 bg-transparent',
        header: 'p-0',
        title: 'sr-only',
        description: 'sr-only',
        body: 'session-drawer-body p-0',
    };

    const handleOpen = (val) => {
        if (val) $bus.emit('drawer-open');
    };

    // The session is fetched lazily, so at @update:open time `session` is still
    // null and every capture went out with session_title: undefined. Wait for the
    // fetch to settle (pending -> false) before recording the open, so the title
    // is actually there. Guarded by session_id because the drawer swaps sessions
    // in place (related sessions) without closing.
    const capturedFor = ref(null);

    const captureOpen = () => {
        const id = props.session_id;
        if (!open.value || !id || pending.value) return;
        if (capturedFor.value === id) return;
        capturedFor.value = id;

        $clientPosthog?.capture('session_drawer_opened', {
            session_id: id,
            session_title: session.value?.title ?? null,
        });
        // Record a pageview against the canonical session URL so the
        // drawer counts the same as a direct visit to /sessions/{id}.
        const slug = id.toLowerCase();
        if (typeof window !== 'undefined') {
            const url = `${window.location.origin}/sessions/${slug}`;
            $clientPosthog?.capture('$pageview', {
                $current_url: url,
                $pathname: `/sessions/${slug}`,
                session_id: slug,
                session_title: session.value?.title ?? null,
                via: 'drawer',
            });
        }
    };

    watch([open, pending, () => props.session_id], captureOpen, { immediate: true });

    const handleClose = () => {
        const currentQuery = { ...route.query };
        // Only strip the drawer query if it still points at a session drawer.
        // If it's already been pushed to a different drawer (e.g. a speaker),
        // leave it alone so that drawer can take over.
        if (typeof currentQuery.drawer === 'string' && /^\/sessions\//.test(currentQuery.drawer)) {
            delete currentQuery.drawer;
            router.replace({ query: currentQuery });
        }
        $bus.emit('drawer-close');
        // Let a reopen of the same session record a fresh open.
        capturedFor.value = null;
        $clientPosthog?.capture('session_drawer_closed', {
            session_id: props.session_id,
        });
    };

    const back = () => {
        if (typeof window !== 'undefined' && window.history.length > 1) window.history.back();
        else open.value = false;
    };

    const formattedSessionTime = computed(() => {
        if (!session.value?.start_date_and_time) return '';
        const tz = getConferenceTimezone(2026);
        const tzLabel = getConferenceTimezoneLabel(2026);
        const start = dayjs(session.value.start_date_and_time).tz(tz);
        const end = session.value.end_date_and_time ? dayjs(session.value.end_date_and_time).tz(tz) : null;
        const dayStr = start.format('ddd');
        const dateStr = start.format('MMM D');
        const startStr = start.format('h:mma');
        const endStr = end ? end.format('h:mma') : '';
        const timeRange = end ? `${startStr} – ${endStr} ${tzLabel}` : `${startStr} ${tzLabel}`;
        // nbsp + space on each side of the bullet so two spaces render (HTML
        // collapses plain double-spaces to one); see detail-card.
        const sep = '  •  ';
        return `${dayStr}${sep}${dateStr}${sep}${timeRange}`;
    });

    const sessionLocation = computed(() =>
        session.value?.locations?.[0]?.name || session.value?.location?.[0]?.name || ''
    );

    const isArchive = computed(() => isArchivedSession(session.value));

    // See header.vue: the glyph's 10px offset only applies when a pill precedes
    // it, otherwise the row starts at the glyph and the margin indents it.
    const hasLeadingPill = computed(() => !!(
        isArchive.value
        || session.value?.session_reservations
        || session.value?.session_type?.[0]?.name
    ));

    const tags2026 = computed(() => {
        const s = session.value;
        if (!s) return [];
        // Session Type is already shown as the eyebrow above the date/time, so
        // it's intentionally omitted from the tag row to avoid duplication.
        const themes = (s.session_theme || []).map(t => ({ ...t, param: `themes=${t.id}` }));
        const roles  = (s.session_role  || []).map(t => ({ ...t, param: `role=${t.id}` }));
        return [...themes, ...roles];
    });
</script>

<template>
    <UDrawer v-model:open="open"
        id="session-drawer"
        class="session-drawer"
        direction="right"
        :dismissable="true"
        :handle="false"
        :handleOnly="true"
        :ui="drawerStyles"
        :title="session?.title || 'Session Details'"
        description="Session information"
        @update:open="handleOpen"
        @close="handleClose">
        <template #body>
            <!-- Title/description come from UDrawer's `:title`/`description` props
                 above (rendered as vaul's sr-only DrawerTitle/DrawerDescription).
                 A hand-rolled reka `DialogTitle` here instead injects
                 `DialogRootContext`, which vaul's drawer doesn't provide — it
                 threw twice on every open in production. -->
            <div v-if="session" ref="contentRef" class="flex flex-col gap-[60px] md:gap-[69px]
                px-[var(--box-padding-lr-m)] md:px-[40px] py-[40px]">

                <!-- Nav bar + Hero container — gap-[40px] between nav and hero -->
                <div class="flex flex-col gap-[40px]">
                    <div class="flex items-center justify-between">
                        <button type="button"
                            @click="back"
                            aria-label="Go back"
                            class="inline-flex items-center gap-[11px] cursor-pointer bg-transparent border-none p-0
                                font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase
                                text-[#7d4566] hover:text-[var(--magenta)] transition-colors">
                            <svg width="7" height="7" viewBox="0 0 8.5 8.5" fill="none" aria-hidden="true">
                                <path d="M7.75 4.25H0.75M0.75 4.25L4.05556 0.750002M0.75 4.25L4.05556 7.75"
                                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            Back
                        </button>
                        <button type="button"
                            @click="open = false"
                            aria-label="Close session details"
                            class="inline-flex items-center justify-center cursor-pointer
                                w-[37px] h-[37px] rounded-full border border-[var(--magenta)] bg-transparent
                                text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white
                                transition-colors">
                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                            </svg>
                        </button>
                    </div>

                    <!-- Hero Section — two columns: content (left) + actions/downloads rail (right) -->
                    <div class="flex flex-col gap-[40px] lg:flex-row lg:items-start lg:gap-[40px]">
                        <div class="flex flex-col gap-[40px] flex-1 min-w-0">
                            <div class="flex flex-col gap-[26px]">
                                <!-- Archive pill (2025) OR limited capacity + session type pills (2026).
                                     Only render when there's a pill to show, otherwise the parent's
                                     gap leaves blank space at the top of the drawer. -->
                                <div v-if="isArchive || session.session_reservations || session.session_type?.[0]?.name || isSessionOnDemand(session)"
                                    class="flex flex-wrap gap-[10px] items-center w-fit">
                                    <span v-if="isArchive"
                                        class="inline-flex w-fit items-center rounded-[25px] border border-(--dark-pink) px-[14px] py-[6px] font-hubspot-sans font-medium text-[12px] leading-[1] tracking-[0.84px] uppercase text-(--dark-pink)">
                                        Archive
                                    </span>
                                    <template v-else>
                                        <span v-if="session.session_reservations"
                                            class="inline-flex items-center rounded-[25px] border border-(--magenta) bg-(--magenta) px-[14px] py-[6px] font-hubspot-sans font-medium text-[12px] leading-[1] tracking-[0.84px] uppercase text-white">
                                            Limited Capacity
                                        </span>
                                        <span v-if="session.session_type?.[0]?.name"
                                            class="inline-flex items-center rounded-[25px] border border-[#7D4566] px-[14px] py-[6px] font-hubspot-sans font-medium text-[12px] leading-[1] tracking-[0.84px] uppercase text-[#7D4566]">
                                            {{ session.session_type[0].name }}
                                        </span>
                                    </template>
                                    <!-- On-demand glyph (Figma 4583:186710 "Union",
                                         15.128 x 9.619). Sits immediately right of
                                         the pills, in --darker-pink, matching the
                                         session-type pill it follows.

                                         ml-[10px] on top of the row's gap-[10px]
                                         makes the 20px the design specifies between
                                         pill and glyph, without widening the gap
                                         BETWEEN the pills, which the design keeps
                                         tighter.

                                         Sized 15x10 rather than the raw 15.128 x
                                         9.619: that is what the three agenda cards
                                         already use for this same glyph, and a
                                         fourth sub-pixel variant would only make the
                                         set inconsistent.

                                         Outside the v-else, so an archived session
                                         gets it too — every session carrying the
                                         flag today is a 2025 archive. -->
                                    <UIcon
                                        v-if="isSessionOnDemand(session)"
                                        name="local-on-demand"
                                        aria-hidden="true"
                                        class="shrink-0 w-[15px] h-[10px] text-[#7D4566]"
                                        :class="hasLeadingPill ? 'ml-[10px]' : ''"
                                    />
                                </div>
                                <p v-if="!isArchive && formattedSessionTime"
                                    class="font-hubspot-sans font-normal text-[14px] leading-[1.5] tracking-[0.21px] text-(--magenta) m-0">
                                    {{ formattedSessionTime }}<template v-if="sessionLocation">&nbsp; •&nbsp; {{ sessionLocation }}</template>
                                </p>
                                <h2 v-html="session.title"
                                    class="font-hubspot-serif font-normal text-[32px] md:text-[48px] leading-[1.2] text-[var(--magenta)] m-0" />
                                <div v-if="session.description" v-html="session.description"
                                    class="session-description font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] text-[var(--magenta)]" />
                                <!-- YouTube embed -->
                                <FragmentsVideoSessionEmbed :embed-id="session.youtube_embed_id" />
                                <div v-if="tags2026.length" class="session-tags-list tags flex flex-row flex-wrap gap-[8px]">
                                    <NuxtLink v-for="tag in tags2026" :key="tag.id" :to="`/agenda?${tag.param}`">
                                        <ProjectTag colorSet="session-tag" :text="tag.name" :useHover="true" :useX="false" gtmEventName="session_tag_clicked" />
                                    </NuxtLink>
                                </div>
                                <SessionsTags v-else :session="session" :tags="[]" />

                                <!-- AI recommendation callout (only when the user has a stored reason) -->
                                <div
                                    v-if="session.recommendation"
                                    data-test="drawer-reason-callout"
                                    class="border border-(--darker-pink) rounded-[10px] px-[19px] py-[17px] flex items-start gap-[10px] w-full">
                                    <svg class="shrink-0 mt-[2px] size-[15px] text-(--darker-pink)" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                                        <circle cx="7.5" cy="7.5" r="7.5" fill="currentColor" />
                                        <path d="M11.5263 8.46995C11.5263 8.54151 11.4683 8.59954 11.3973 8.59954C10.264 8.59954 8.92285 9.94044 8.92285 11.0734C8.92285 11.145 8.86481 11.203 8.7938 11.203C8.7228 11.203 8.66476 11.145 8.66476 11.0734C8.66419 9.94044 7.32302 8.59954 6.18978 8.59954C6.11821 8.59954 6.06073 8.54151 6.06073 8.46995C6.06073 8.3984 6.11877 8.34037 6.18978 8.34037C7.32302 8.34037 8.66419 6.99947 8.66419 5.86703C8.66419 5.79547 8.72224 5.73744 8.79324 5.73744C8.86424 5.73744 8.92229 5.79547 8.92229 5.86703C8.92229 6.99947 10.2635 8.34037 11.3967 8.34037C11.4683 8.34037 11.5258 8.3984 11.5258 8.46995H11.5263Z" fill="white" />
                                        <path d="M7.2753 5.73726C7.2753 5.78496 7.23661 5.82364 7.18927 5.82364C6.43378 5.82364 5.53966 6.71758 5.53966 7.47292C5.53966 7.52062 5.50096 7.55931 5.45363 7.55931C5.40629 7.55931 5.3676 7.52062 5.3676 7.47292C5.36722 6.71758 4.4731 5.82364 3.71761 5.82364C3.6699 5.82364 3.63158 5.78496 3.63158 5.73726C3.63158 5.68955 3.67027 5.65087 3.71761 5.65087C4.4731 5.65087 5.36722 4.75693 5.36722 4.00197C5.36722 3.95427 5.40592 3.91558 5.45325 3.91558C5.50059 3.91558 5.53928 3.95427 5.53928 4.00197C5.53928 4.75693 6.4334 5.65087 7.1889 5.65087C7.23661 5.65087 7.27493 5.68955 7.27493 5.73726H7.2753Z" fill="white" />
                                    </svg>
                                    <p class="flex-1 font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--darker-pink) m-0">{{ session.recommendation }}</p>
                                </div>
                            </div>
                            <div class="flex flex-col gap-[20px] md:flex-row md:items-center md:gap-[39px]">
                                <FragmentsSponsorsSponsoredBy :sponsors="session.sponsors" />
                                <FragmentsShare :title="session.title" :link="shareLink" label="Share:" copyToClipboard />
                            </div>
                        </div>

                        <!-- Client-only: the sidebar's variant is driven entirely by
                             per-user state (user.scheduled / user.sessionRegistration). On a
                             direct ?drawer= URL the drawer is SSR-rendered with empty user
                             state, and the root class binding gets stuck after hydration
                             (white instead of the orange action-required nudge). Same fix as
                             the detail page. -->
                        <ClientOnly>
                            <FragmentsDrawersSessionSidebar v-if="!isArchive" :session="session" class="w-full lg:w-[270px] lg:shrink-0" />
                        </ClientOnly>
                    </div>
                </div>

                <SessionsSpeakers v-if="session.speakers?.length" :session="session" variant="drawer" />

                <SessionsRelated :session="session" variant="drawer" />
            </div>

            <div v-else-if="pending" class="flex items-center justify-center min-h-[50vh]">
                <FragmentsLoading loading />
            </div>
        </template>
    </UDrawer>
</template>

<style lang="scss">
.session-drawer {
    z-index: 0;
}

.session-drawer .session-description :is(p, ul, ol, dl, blockquote):not(:last-child) {
    margin-bottom: 1rem;
}

.session-drawer .session-description :is(ul, ol) {
    padding-left: 1.25rem;
    list-style: revert;
}

/* SponsoredBy carries a global `margin-bottom: 2rem` at ≥1024px. In the share
   row (flex, items-center) that bottom margin shifts the logo's centering point
   down, so the sponsor logo sits above the share links instead of centered with
   them. Zero it here, mirroring the session detail page's .session-hero-share
   override. */
.session-drawer .sponsored-by {
    margin-bottom: 0;
}
</style>
